import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
  ArrowRight,
  CalendarClock,
  Check,
  ChevronDown,
  Eye,
  Lock,
  Pause,
  Pencil,
  Play,
  Plus,
  Share2,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import CreateScheduleModal from "./CreateScheduleModal";
import ShareChatModal from "../../../../Chat/components/ShareChatModal";
import useRunTask from "../../../hooks/useRunTask";
import ToolIcon from "../../../../Chat/components/WelcomeSections/ToolIcon";
import { MicIcon } from "../../../../Chat/components/WelcomeSections/InputIcons";
import Uploadfile from "../../../../Chat/components/uploadFiles/index";
import slackIcon from "../../../../../assets/Images/Slack.png";
import teamsIcon from "../../../../../assets/Images/MicrosoftTeams.png";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { useUserInfo } from "../../../../../context/user";
import { useWebhookConfigs, useCreateWebhookConfig } from "../../../../../queries/webhooks";
import {
  createProviderSelectOption,
  DEFAULT_OUTPUT_OPTION,
  isChannelProvider,
  parseCalendarDate,
} from "../utils";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderColor: "hsl(var(--border))",
    boxShadow: "none",
    fontSize: 14,
    borderRadius: 8,
    backgroundColor: "hsl(var(--background))",
  }),
  menu: (base) => ({ ...base, zIndex: 10, backgroundColor: "hsl(var(--popover))" }),
  singleValue: (base) => ({ ...base, color: "hsl(var(--foreground))" }),
  input: (base) => ({ ...base, color: "hsl(var(--foreground))" }),
};

/* Compact variant of the react-select control so the channel picker reads as
   one of the inline chips in the "When ... send the result to ..." sentence. */
const chipSelectStyles = {
  ...selectStyles,
  control: (base, state) => ({
    ...base,
    minHeight: 36,
    height: 36,
    minWidth: 190,
    borderRadius: 6,
    fontSize: 14,
    boxShadow: "none",
    backgroundColor: "hsl(var(--background))",
    borderColor: state.isFocused
      ? "hsl(var(--foreground))"
      : "hsl(var(--border))",
    "&:hover": { borderColor: "hsl(var(--foreground))" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
};

const SUGGESTED_SCHEDULES = [
  "Schedule Daily",
  "Schedule Weekly",
  "Schedule Monthly",
  "Schedule Annually",
];

/* Deterministic suggested cadence, ported from Lovable's
   lib/suggestAutomation.ts. Webhook is reserved for Tasks whose copy clearly
   reacts to an incoming event; everything else maps to a schedule bucket via a
   stable hash so the label doesn't shuffle between renders. */
const suggestAutomation = (title, promptHtml) => {
  const blob = `${title || ""} ${(promptHtml || "").replace(/<[^>]*>/g, " ")}`
    .toLowerCase()
    .trim();
  if (
    /(webhook|incoming|received|reply|inbound|when .* arrives|on new|trigger)/.test(
      blob,
    )
  ) {
    return "By Webhook";
  }
  let hash = 0;
  for (let index = 0; index < blob.length; index += 1) {
    hash = (hash * 31 + blob.charCodeAt(index)) | 0;
  }
  return SUGGESTED_SCHEDULES[Math.abs(hash) % SUGGESTED_SCHEDULES.length];
};

const formatRelativeTime = (timestamp) => {
  if (!timestamp) return null;
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const useRelativeTime = (timestamp) => {
  const [, force] = useState(0);
  useEffect(() => {
    if (!timestamp) return undefined;
    const interval = setInterval(() => force((tick) => tick + 1), 30000);
    return () => clearInterval(interval);
  }, [timestamp]);
  return formatRelativeTime(timestamp);
};

const useTick = (intervalMs) => {
  const [, force] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => force((tick) => tick + 1), intervalMs);
    return () => clearInterval(interval);
  }, [intervalMs]);
};

const FULL_DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const ORDINAL_INDEX = {
  "1st": 1,
  "2nd": 2,
  "3rd": 3,
  "4th": 4,
  "5th": 5,
  Last: -1,
};

const parseTimeParts = (time) => {
  if (!time || typeof time !== "string") return { hours: 9, minutes: 0 };
  const [hh, mm] = time.split(":");
  return {
    hours: Math.max(0, Math.min(23, Number(hh) || 0)),
    minutes: Math.max(0, Math.min(59, Number(mm) || 0)),
  };
};

const setTimeOnDate = (date, time) => {
  const { hours, minutes } = parseTimeParts(time);
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const computeOrdinalWeekdayInMonth = (year, month, ordinal, weekdayIndex) => {
  if (ordinal === -1) {
    const lastOfMonth = new Date(year, month + 1, 0);
    const offset = (lastOfMonth.getDay() - weekdayIndex + 7) % 7;
    return new Date(year, month, lastOfMonth.getDate() - offset);
  }
  const firstOfMonth = new Date(year, month, 1);
  const offset = (weekdayIndex - firstOfMonth.getDay() + 7) % 7;
  const day = 1 + offset + (ordinal - 1) * 7;
  const result = new Date(year, month, day);
  return result.getMonth() === month ? result : null;
};

const parseScheduleDate = (scheduleDate) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(scheduleDate || "")) return null;
  const [year, month, day] = scheduleDate.split("-").map(Number);
  const candidate = new Date(year, month - 1, day);
  if (
    candidate.getFullYear() !== year ||
    candidate.getMonth() !== month - 1 ||
    candidate.getDate() !== day
  ) {
    return null;
  }
  return { year, month: month - 1, day };
};

const computeNextRun = (config) => {
  if (!config) return null;
  const {
    repeat,
    weekDays,
    monthlyMode,
    weekOrdinal = "1st",
    weekdayName = "Monday",
    scheduleDate,
    time = "09:00",
  } = config;

  const parsedDate = parseScheduleDate(scheduleDate);
  if ((repeat === "monthly" || repeat === "yearly") && !parsedDate) {
    return null;
  }
  const dayOfMonth = parsedDate?.day ?? 1;
  const yearMonth = parsedDate ? MONTH_NAMES[parsedDate.month] : "January";

  const now = new Date();

  if (repeat === "daily") {
    const candidate = setTimeOnDate(new Date(now), time);
    if (candidate <= now) candidate.setDate(candidate.getDate() + 1);
    return candidate;
  }

  if (repeat === "weekly") {
    const days = (weekDays || []).map((day) => FULL_DAY_NAMES.indexOf(day)).filter((index) => index >= 0);
    if (!days.length) return null;
    for (let offset = 0; offset < 14; offset += 1) {
      const candidate = setTimeOnDate(new Date(now), time);
      candidate.setDate(candidate.getDate() + offset);
      if (days.includes(candidate.getDay()) && candidate > now) return candidate;
    }
    return null;
  }

  if (repeat === "monthly") {
    for (let monthOffset = 0; monthOffset < 24; monthOffset += 1) {
      const baseYear = now.getFullYear();
      const baseMonth = now.getMonth() + monthOffset;
      let candidate;
      if (monthlyMode === "dayOfWeek") {
        const ordinalValue = ORDINAL_INDEX[weekOrdinal] ?? 1;
        const weekdayIndex = FULL_DAY_NAMES.indexOf(weekdayName);
        candidate = computeOrdinalWeekdayInMonth(
          baseYear,
          baseMonth,
          ordinalValue,
          weekdayIndex >= 0 ? weekdayIndex : 1,
        );
        if (!candidate) continue;
      } else {
        candidate = new Date(baseYear, baseMonth, dayOfMonth);
        if (candidate.getMonth() !== ((baseMonth % 12) + 12) % 12) continue;
      }
      setTimeOnDate(candidate, time);
      if (candidate > now) return candidate;
    }
    return null;
  }

  if (repeat === "yearly") {
    const targetMonth = MONTH_NAMES.indexOf(yearMonth);
    if (targetMonth < 0) return null;
    for (let yearOffset = 0; yearOffset < 5; yearOffset += 1) {
      const year = now.getFullYear() + yearOffset;
      let candidate;
      if (monthlyMode === "dayOfWeek") {
        const ordinalValue = ORDINAL_INDEX[weekOrdinal] ?? 1;
        const weekdayIndex = FULL_DAY_NAMES.indexOf(weekdayName);
        candidate = computeOrdinalWeekdayInMonth(
          year,
          targetMonth,
          ordinalValue,
          weekdayIndex >= 0 ? weekdayIndex : 1,
        );
        if (!candidate) continue;
      } else {
        candidate = new Date(year, targetMonth, dayOfMonth);
        if (candidate.getMonth() !== targetMonth) continue;
      }
      setTimeOnDate(candidate, time);
      if (candidate > now) return candidate;
    }
    return null;
  }

  return null;
};

const formatClockTime = (date) => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours >= 12 ? "pm" : "am";
  hours = hours % 12 || 12;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}${meridiem}`;
};

const formatNextRun = (date) => {
  if (!date) return null;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const dayDiff = Math.round(
    (startOfDate.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24),
  );
  const time = formatClockTime(date);
  if (dayDiff === 0) return `Today ${time}`;
  if (dayDiff === 1) return `Tomorrow ${time}`;
  if (dayDiff > 1 && dayDiff < 7) return `${FULL_DAY_NAMES[date.getDay()]} ${time}`;
  return `${SHORT_MONTHS[date.getMonth()]} ${date.getDate()} ${time}`;
};

const PromptDetailsForm = ({
  isEdit,
  promptName,
  promptNameError,
  onPromptNameChange,
  isScheduleActive,
  onSchedulePause,
  outputOption,
  requiredIntegrations,
  channelList,
  slackChannel,
  onOutputOptionChange,
  onSlackChannelChange,
  onChannelSearchInputChange,
  onChannelMenuScrollToBottom,
  loadingChannels,
  loadingMoreChannels,
  channelSearchQuery,
  scheduleFrequency,
  scheduleTime,
  scheduleDate,
  scheduleRunCount,
  selectedWeekDays,
  onScheduleFrequencyChange,
  onScheduleTimeChange,
  onScheduleDateChange,
  onScheduleRunCountChange,
  onSelectedWeekDaysChange,
  promptContent,
  promptContentError,
  onPromptContentChange,
  promptSetAs,
  onPromptSetAsChange,
  estimatedTime,
  onEstimatedTimeChange,
  processing,
  onSubmit,
  onDeleteClick,
  lastSavedAt,
  editadata,
  getUpdatePromptsData,
  selectedWebhookId,
  onSelectedWebhookIdChange,
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { runTask, running: runningTask } = useRunTask();
  const [attachedFiles, setAttachedFiles] = useState([]);
  // Drives the right-rail "Tested" row: flips once this Task has been run
  // from the page.
  const [hasRun, setHasRun] = useState(false);
  // The trigger chip reads "I run it" until the user picks a trigger, matching
  // Lovable's null-trigger state (our schedule model has no separate "unset").
  const [triggerPicked, setTriggerPicked] = useState(false);

  const handleRunNow = async () => {
    if (!isEdit) return;
    // runTask swallows its own errors, so only flip "Tested" when it reports
    // that the run actually went through.
    const ran = await runTask(editadata);
    if (ran) setHasRun(true);
  };

  // Voice input — same react-speech-recognition flow as the New Task composer
  // (PromptIntroStep); the transcript diff is appended to the prompt.
  const { transcript, listening, browserSupportsSpeechRecognition } =
    useSpeechRecognition();
  const processedTranscriptLen = useRef(0);
  useEffect(() => {
    if (transcript.length <= processedTranscriptLen.current) return;
    const diff = transcript.slice(processedTranscriptLen.current);
    processedTranscriptLen.current = transcript.length;
    onPromptContentChange?.(`${promptContent || ""}${diff}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transcript]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleConfig, setScheduleConfig] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  // Placeholder strapline under the Task name. Editable in the rename editor
  // like Lovable's taskDescription, but there is no field for it on a Task yet,
  // so it isn't persisted (same as the static line it replaces).
  const [taskSubtitle, setTaskSubtitle] = useState(
    "Summary, decisions, and action owners",
  );
  const nameBeforeEditRef = useRef("");
  const subtitleBeforeEditRef = useRef("");

  useEffect(() => {
    if (
      !scheduleFrequency ||
      scheduleFrequency === "none" ||
      scheduleFrequency === "webhook"
    ) {
      return;
    }
    // Monthly/yearly cadence is stored as a YYYY-MM-DD string; unpack it so
    // computeNextRun gets the real day/month instead of its 1-January defaults.
    // parseCalendarDate reads it as a local date — `new Date("2026-03-01")` is
    // UTC midnight and would land on 28 Feb west of UTC.
    const parsedDate = parseCalendarDate(scheduleDate);
    setScheduleConfig({
      repeat: scheduleFrequency,
      weekDays: Array.isArray(selectedWeekDays) ? selectedWeekDays : [],
      scheduleDate: scheduleDate || "",
      runCount: scheduleRunCount || 1,
      // Sanitise here so both the summary and the "Next run" maths see a real
      // HH:mm even when the stored row is unparseable.
      time: /^\d{1,2}:\d{2}$/.test(scheduleTime || "")
        ? scheduleTime
        : "09:00",
      ...(parsedDate
        ? {
            dayOfMonth: parsedDate.day,
            yearMonth: MONTH_NAMES[parsedDate.monthIndex],
          }
        : {}),
    });
  }, [scheduleFrequency, scheduleTime, scheduleDate, scheduleRunCount, selectedWeekDays]);
  const nameInputRef = useRef(null);
  const quillRef = useRef(null);
  const lastPastedHtmlRef = useRef(null);
  const savedAgo = useRelativeTime(lastSavedAt);

  useEffect(() => {
    const el = quillRef.current;
    if (!el) return;
    const html = promptContent || "";
    if (lastPastedHtmlRef.current === html) return;
    if (el.innerHTML === html) {
      lastPastedHtmlRef.current = html;
      return;
    }
    el.innerHTML = html;
    lastPastedHtmlRef.current = html;
  }, [promptContent]);

  const webhookUserInfo = useUserInfo();
  const webhookUser = webhookUserInfo[0];
  const webhookLocation = webhookUserInfo[4];
  const webhookUserId = webhookUser?._id;
  const webhookAccountId = webhookLocation?.id;

  const [webhookError, setWebhookError] = useState("");
  const setSelectedWebhookId = onSelectedWebhookIdChange || (() => { });
  const [showCreateWebhook, setShowCreateWebhook] = useState(false);
  const [showWebhookMenu, setShowWebhookMenu] = useState(false);
  const [newWebhookName, setNewWebhookName] = useState("");
  const webhookMenuRef = useRef(null);

  const { data: webhooks = [], isLoading: webhooksLoading } = useWebhookConfigs(
    webhookUserId,
    webhookAccountId,
  );
  const createWebhook = useCreateWebhookConfig(webhookUserId, webhookAccountId);
  const creatingWebhook = createWebhook.isPending;

  useEffect(() => {
    if (!showWebhookMenu) return undefined;
    const handleDocumentClick = (event) => {
      if (
        webhookMenuRef.current &&
        !webhookMenuRef.current.contains(event.target)
      ) {
        setShowWebhookMenu(false);
      }
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [showWebhookMenu]);

  const selectedWebhook = webhooks.find(
    (hook) => hook.source_id === selectedWebhookId,
  );

  const handleCreateWebhook = async () => {
    const trimmed = newWebhookName.trim();
    if (!trimmed || !webhookUserId || creatingWebhook) return;
    setWebhookError("");
    let created;
    try {
      created = await createWebhook.mutateAsync(trimmed);
    } catch (error) {
      setWebhookError(error?.message || "Failed to create webhook");
      return;
    }
    if (created) {
      setSelectedWebhookId(created.source_id);
    }
    setNewWebhookName("");
    setShowCreateWebhook(false);
    setShowWebhookMenu(false);
  };

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select?.();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (!isEdit) return;
    console.log("[PromptDetailsForm] Edit data:", {
      editadata,
      promptName,
      promptContent,
      promptSetAs,
      outputOption,
      slackChannel,
      scheduleFrequency,
      scheduleTime,
      scheduleDate,
      scheduleRunCount,
      selectedWeekDays,
      isScheduleActive,
      lastSavedAt,
    });
  }, [
    isEdit,
    editadata,
    promptName,
    promptContent,
    promptSetAs,
    outputOption,
    slackChannel,
    scheduleFrequency,
    scheduleTime,
    scheduleDate,
    scheduleRunCount,
    selectedWeekDays,
    isScheduleActive,
    lastSavedAt,
  ]);

  const startNameEdit = () => {
    nameBeforeEditRef.current = promptName;
    subtitleBeforeEditRef.current = taskSubtitle;
    setIsEditingName(true);
  };

  const commitNameEdit = () => setIsEditingName(false);

  // Escape / ✕ puts the name and subtitle back to what they were when the
  // editor opened, so Cancel actually cancels.
  const cancelNameEdit = () => {
    onPromptNameChange(nameBeforeEditRef.current ?? "");
    setTaskSubtitle(subtitleBeforeEditRef.current ?? "");
    setIsEditingName(false);
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      commitNameEdit();
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelNameEdit();
    }
  };

  const showChannelPicker = isChannelProvider(outputOption?.value);
  const selectedChannel = channelList.find(
    (channel) => channel.channel_id === slackChannel,
  );
  const selectedChannelOption = selectedChannel
    ? {
      value: selectedChannel.channel_id,
      label: `#${selectedChannel.channel_name}`,
    }
    : null;

  const providerOptions = useMemo(() => {
    const fromIntegrations = [...requiredIntegrations]
      .sort((a, b) =>
        (a.provider_name || "").localeCompare(b.provider_name || ""),
      )
      .map(createProviderSelectOption);

    const opts = [...fromIntegrations, DEFAULT_OUTPUT_OPTION];

    if (
      outputOption?.value &&
      outputOption.value !== DEFAULT_OUTPUT_OPTION.value &&
      !opts.some((opt) => opt.value === outputOption.value)
    ) {
      opts.unshift(createProviderSelectOption({ provider_id: outputOption.value }));
    }

    return opts;
  }, [requiredIntegrations, outputOption]);

  const triggerType =
    scheduleFrequency === "webhook"
      ? "webhook"
      : !scheduleFrequency || scheduleFrequency === "none"
        ? "manual"
        : "scheduled";

  const handleTriggerChange = (type) => {
    if (type === "manual") {
      onScheduleFrequencyChange("none");
    } else if (type === "scheduled" && triggerType !== "scheduled") {
      onScheduleFrequencyChange("weekly");
    } else if (type === "webhook") {
      onScheduleFrequencyChange("webhook");
    }
  };

  const formatScheduleSummary = () => {
    if (!scheduleConfig) return null;
    const { repeat, weekDays, runCount, scheduleDate: cfgDate, time } = scheduleConfig;
    // Only trust a real HH:mm — older rows can carry junk like "Invalid date".
    const displayTime = /^\d{1,2}:\d{2}$/.test(time || "") ? time : "09:00";

    if (repeat === "daily") {
      return `Daily | ${displayTime}`;
    }
    if (repeat === "weekly") {
      if (!weekDays?.length) return `Add Schedule`;
      const oneDay = weekDays.length === 1 ? `Every ${weekDays[0]}` : weekDays.map((d) => d.slice(0, 3)).join(", ");
      const prefix = runCount > 1 ? `Every ${runCount} weeks: ` : "";
      return `${prefix}${oneDay} | ${displayTime}`;
    }
    if (repeat === "monthly") {
      if (!cfgDate) return "Add Schedule";
      const prefix = runCount > 1 ? `Every ${runCount} months on ` : "Monthly on ";
      return `${prefix}${cfgDate} | ${displayTime}`;
    }
    if (repeat === "yearly") {
      if (!cfgDate) return "Add Schedule";
      const prefix = runCount > 1 ? `Every ${runCount} years on ` : "Yearly on ";
      return `${prefix}${cfgDate} | ${displayTime}`;
    }
    return null;
  };

  const handleScheduleCreate = (config) => {
    setScheduleConfig(config);
    onScheduleFrequencyChange?.(config.repeat);
    onScheduleTimeChange?.(config.time);
    onScheduleDateChange?.(config.scheduleDate || "");
    onScheduleRunCountChange?.(config.runCount || 1);
    onSelectedWeekDaysChange?.(config.weekDays || []);
    setShowScheduleModal(false);
  };

  const scheduleSummary = formatScheduleSummary();
  useTick(60000);
  const nextRunLabel = formatNextRun(computeNextRun(scheduleConfig));

  const saveLabel = processing
    ? "Saving…"
    : isEdit
      ? "Save Task"
      : "Create Task";
  const justSaved = savedAgo === "just now";
  // Lovable stamps the save with an absolute date+time rather than "5m ago".
  const savedAtLabel = lastSavedAt
    ? `Saved ${new Date(lastSavedAt).toLocaleDateString([], {
        day: "2-digit",
        month: "short",
      })} ${new Date(lastSavedAt).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })}`
    : null;
  const outputChipLabel = showChannelPicker
    ? outputOption?.name || DEFAULT_OUTPUT_OPTION.label
    : DEFAULT_OUTPUT_OPTION.label;
  const triggerChipLabel =
    triggerType === "scheduled"
      ? "Schedule"
      : triggerType === "webhook"
        ? "Webhook"
        : triggerPicked
          ? "Manual"
          : "I run it";
  const triggerClause =
    triggerType === "scheduled"
      ? "runs the Task,"
      : triggerType === "webhook"
        ? "fires,"
        : "triggers it,";
  const hasSchedule =
    Boolean(scheduleSummary) && scheduleSummary !== "Add Schedule";
  // A Task loaded with a schedule/webhook already has its trigger set; only a
  // frequency of "none" that the user hasn't touched counts as "not chosen yet".
  const triggerIsSet = triggerPicked || triggerType !== "manual";
  // shared_with is the backend-computed list of independent copies made from
  // this Task (see ShareScheduledPrompt) — fall back to the legacy userId
  // "shared" entries for any Task shared before that change shipped.
  const sharedUsers =
  
    Array.isArray(editadata?.shared_with) && editadata.shared_with.length
      ? editadata.shared_with
      : (editadata?.userId || []).filter((user) => user?.type === "shared");
  const visibleSharedUsers = sharedUsers.slice(0, 5);
  const remainingSharedUsers = sharedUsers.length - visibleSharedUsers.length;

  // "Remove" must clear the cadence AND the frequency. Leaving the frequency
  // set meant triggerType stayed "scheduled", the effect above rebuilt
  // scheduleConfig from the surviving props (so the schedule reappeared as
  // "Add Schedule"), and Save still persisted `repeat: weekly` with no days or
  // time. Resetting to "none" also clears `active` and `utcTime` in the parent.
  const clearSchedule = () => {
    setScheduleConfig(null);
    onScheduleFrequencyChange?.("none");
    onScheduleRunCountChange?.(1);
    onScheduleTimeChange?.("");
    onSelectedWeekDaysChange?.([]);
    onScheduleDateChange?.("");
  };

  const automateDone =
    triggerType === "manual"
      ? true
      : triggerType === "scheduled"
        ? hasSchedule
        : Boolean(selectedWebhookId);

  // Right-rail progress rows — same labels and order as the Lovable
  // "Building your Task" card, fed from this Task's real state.
  const railRows = [
    {
      label: isEdit ? "Edit Task" : "Create Task",
      value: promptName ? "Drafted" : "Untitled",
      done: Boolean(promptName),
    },
    { label: "Tested", value: hasRun ? "Done" : "Pending", done: hasRun },
    {
      label: "Saved",
      value: savedAgo ? "Saved" : "Pending",
      done: Boolean(savedAgo),
    },
    {
      label: "Automate",
      value: !triggerIsSet
        ? outputChipLabel
        : triggerType === "scheduled"
          ? hasSchedule
            ? scheduleSummary
            : "Schedule"
          : triggerType === "webhook"
            ? selectedWebhook?.webhook_name || "Webhook"
            : "Manual",
      done: triggerIsSet && automateDone,
    },
    {
      label: "Sharing",
      value: promptSetAs === "shared" ? "Visible to team" : "Private",
      done: promptSetAs === "shared",
    },
    {
      // No "confirmed" flag is persisted for a Task, so the estimate never
      // graduates to confirmed the way it does on Lovable.
      label: "Time Saved",
      value: `${estimatedTime || 0}h (estimate)`,
      done: false,
    },
  ];

  return (
    <TooltipProvider delayDuration={150}>
      <div className="w-full">
        {isEdit && (
          <>
            {/* Inline-flex so the eyebrow sits on the same line as the
                "Return to Tasks" link above it, exactly as on Lovable
                /edit-schedule. */}
            <span className="page-eyebrow">Editing Task</span>
            <h1 className="page-display mb-8 text-3xl md:text-4xl">
              Edit and <span className="accent block md:inline">automate</span>
            </h1>
          </>
        )}

        <div className="flex gap-10">
          <div className="min-w-0 flex-1">
            {/* Unified Task card */}
            <div className="mb-6">
              <div className="group relative w-full rounded-3xl bg-card shadow-[0_20px_50px_hsl(var(--foreground)/0.04)] ring-1 ring-foreground/5 transition-all duration-500 focus-within:shadow-[0_20px_50px_hsl(var(--task-accent)/0.10)] focus-within:ring-2 focus-within:ring-[hsl(var(--task-accent)/0.25)]">
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 px-5 pt-6 pb-2 sm:px-8">
                  <div className="min-w-0 flex-1">
                    {isEditingName ? (
                      <div className="flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                          {/* Explicit size utilities rather than .text-heading:
                              the Input base ships md:text-sm, which out-ranks a
                              component-layer class and would shrink the field. */}
                          <Input
                            ref={nameInputRef}
                            type="text"
                            value={promptName}
                            onChange={(event) =>
                              onPromptNameChange(event.target.value)
                            }
                            onKeyDown={handleNameKeyDown}
                            placeholder="Untitled Task"
                            className="font-mono-display h-auto flex-1 rounded-md border border-border bg-background px-2 py-1 text-[20px] font-semibold tracking-[-0.01em] focus-visible:ring-2 focus-visible:ring-[hsl(var(--task-accent)/0.25)] md:text-[20px]"
                          />
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={commitNameEdit}
                                aria-label="Save"
                                className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-foreground bg-foreground text-background hover:bg-foreground/90"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Save (Enter)</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={cancelNameEdit}
                                aria-label="Cancel"
                                className="grid h-8 w-8 shrink-0 place-items-center rounded-md border border-border text-muted-foreground hover:bg-muted"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Cancel (Esc)</TooltipContent>
                          </Tooltip>
                        </div>
                        {/* Subtitle field (Lovable's editable taskDescription).
                            Commented out until a Task actually has a
                            description/subtitle field to save it to — the
                            strapline below is placeholder copy for now.
                        <Input
                          type="text"
                          value={taskSubtitle}
                          onChange={(event) => setTaskSubtitle(event.target.value)}
                          onKeyDown={handleNameKeyDown}
                          className="h-auto rounded-md border border-border bg-background px-2 py-1 text-sm font-light text-muted-foreground focus-visible:ring-2 focus-visible:ring-[hsl(var(--task-accent)/0.25)]"
                        />
                        */}
                      </div>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <h2 className="font-mono-display text-heading text-foreground">
                            {promptName || (
                              <span className="font-normal text-muted-foreground">
                                Untitled Task
                              </span>
                            )}
                          </h2>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={startNameEdit}
                                aria-label="Edit Task name"
                                className="text-muted-foreground hover:text-foreground"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Rename this Task</TooltipContent>
                          </Tooltip>
                          {savedAtLabel && (
                            <span
                              className="ml-1 flex items-center gap-1 whitespace-nowrap text-xs font-light text-muted-foreground"
                              aria-live="polite"
                            >
                              <span className="text-emerald-600">✓</span>
                              {savedAtLabel}
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm font-light text-muted-foreground">
                          {taskSubtitle}
                        </p>
                      </>
                    )}
                    {promptNameError && (
                      <small className="mt-1 block text-destructive">
                        {promptNameError}
                      </small>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onSubmit}
                    disabled={processing}
                    aria-label={saveLabel}
                    className="inline-flex h-10 shrink-0 items-center gap-2 rounded-lg border border-foreground/80 bg-background px-4 text-sm font-medium text-foreground transition-all hover:bg-foreground hover:text-background active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-background disabled:hover:text-foreground disabled:active:scale-100"
                  >
                    {justSaved ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    {saveLabel}
                  </button>
                </div>

                <div className="px-5 pb-4 sm:px-8">
                  <div className="prompt-quill-wrapper prompt-quill-wrapper--bare">
                    <div
                      ref={quillRef}
                      contentEditable
                      suppressContentEditableWarning
                      className="prompt-html-editor ql-editor"
                      data-placeholder="Describe what this task should do..."
                      onInput={(event) => {
                        const html = event.currentTarget.innerHTML;
                        lastPastedHtmlRef.current = html;
                        onPromptContentChange?.(html);
                      }}
                      onBlur={(event) => {
                        const html = event.currentTarget.innerHTML;
                        lastPastedHtmlRef.current = html;
                        onPromptContentChange?.(html);
                      }}
                    />
                  </div>
                  {promptContentError && (
                    <small className="text-destructive">
                      {promptContentError}
                    </small>
                  )}
                </div>

                {/* Footer band: attach on the left, voice + run on the right —
                    the Lovable /edit-schedule composer toolbar. */}
                <div className="flex flex-wrap items-center gap-3 border-t border-border/60 bg-muted/40 px-5 py-4 sm:px-8 sm:py-5 rounded-b-3xl">
                  <div className="flex items-center gap-2">
                    <Uploadfile
                      classname="!outline-none shadow-none"
                      chatId={undefined}
                      onFileSelect={(files) =>
                        setAttachedFiles((current) => [
                          ...current,
                          ...(files || []),
                        ])
                      }
                      onRunNow={isEdit ? handleRunNow : undefined}
                      onAutomate={onSubmit}
                    />
                    {attachedFiles.length > 0 && (
                      <span className="text-xs font-light text-muted-foreground">
                        {attachedFiles.length} attached
                      </span>
                    )}
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    {browserSupportsSpeechRecognition && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            aria-label="Voice input"
                            onClick={() =>
                              listening
                                ? SpeechRecognition.stopListening()
                                : SpeechRecognition.startListening({
                                    continuous: true,
                                  })
                            }
                            className={`grid h-9 w-9 place-items-center rounded-md transition-colors ${
                              listening
                                ? "text-foreground"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            <MicIcon size={18} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {listening ? "Stop listening" : "Voice input"}
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleRunNow}
                          disabled={!isEdit || runningTask}
                          aria-label="Run this Task now"
                          className="grid h-10 w-10 place-items-center rounded-md border border-foreground bg-foreground text-background shadow-sm transition-colors hover:bg-foreground/90 disabled:cursor-not-allowed disabled:border-border disabled:bg-muted/40 disabled:text-muted-foreground"
                        >
                          <ArrowRight className="h-[18px] w-[18px]" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {runningTask
                          ? "Running…"
                          : isEdit
                            ? "Run this Task now"
                            : "Save the Task before running"}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </div>

            <div className="page-eyebrow mb-4">How to automate this Task</div>

            {/* Editorial recipe: one sentence, inline editable chips. */}
            <div
              id="automate-section"
              className="interactive-surface mb-6 rounded-2xl border border-border bg-card p-5"
            >
              <div className="mb-4 flex items-center gap-2 border-b border-border/60 pb-3 text-sm font-medium text-foreground">
                <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Select the trigger and schedule for this Task</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-3 text-[15px] font-light leading-[2.2] text-muted-foreground">
                <span>When</span>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="interactive-chip inline-flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground"
                    >
                      {triggerType === "scheduled" ? (
                        <CalendarClock className="h-3.5 w-3.5" />
                      ) : triggerType === "webhook" ? (
                        <Sparkles className="h-3.5 w-3.5" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                      {triggerChipLabel}
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[220px]">
                    <DropdownMenuItem
                      onSelect={() => {
                        setTriggerPicked(true);
                        handleTriggerChange("manual");
                      }}
                      className="gap-2"
                    >
                      <Play className="h-4 w-4" /> Manual
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        On demand
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        setTriggerPicked(true);
                        handleTriggerChange("scheduled");
                        if (!hasSchedule) setShowScheduleModal(true);
                      }}
                      className="gap-2"
                    >
                      <CalendarClock className="h-4 w-4" /> Schedule
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        Recurring
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={() => {
                        setTriggerPicked(true);
                        handleTriggerChange("webhook");
                      }}
                      className="gap-2"
                    >
                      <Sparkles className="h-4 w-4" /> Webhook
                      <span className="ml-auto text-[11px] text-muted-foreground">
                        Event
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <span>{triggerClause}</span>
                <span>send the result to</span>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="interactive-chip inline-flex h-9 items-center gap-2 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground"
                    >
                      {showChannelPicker && outputOption?.value ? (
                        <ToolIcon type={outputOption.value} />
                      ) : (
                        /* The Maybe* asterisk, as on Lovable's "Your Tasks" chip. */
                        <span
                          aria-hidden
                          className="font-mono-display inline-flex h-3.5 w-3.5 items-center justify-center font-bold leading-none"
                        >
                          *
                        </span>
                      )}
                      {outputChipLabel}
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-[260px]">
                    {providerOptions.map((option) => (
                      <DropdownMenuItem
                        key={option.value}
                        onSelect={() => onOutputOptionChange(option)}
                        className="gap-2"
                      >
                        {option.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                {showChannelPicker && (
                  <>
                    <span>in</span>
                    <Select
                      placeholder="Search a channel"
                      value={selectedChannelOption}
                      onChange={onSlackChannelChange}
                      options={channelList.map((channel) => ({
                        value: channel.channel_id,
                        label: `#${channel.channel_name}`,
                      }))}
                      onInputChange={onChannelSearchInputChange}
                      onMenuScrollToBottom={onChannelMenuScrollToBottom}
                      isLoading={loadingChannels || loadingMoreChannels}
                      filterOption={null}
                      styles={chipSelectStyles}
                      noOptionsMessage={() =>
                        loadingChannels
                          ? "Loading channels..."
                          : channelSearchQuery
                            ? "No channels found"
                            : "No channels available"
                      }
                    />
                  </>
                )}

                <span className="text-muted-foreground/60">.</span>
              </div>

              {/* Single detail strip under the sentence. */}
              <div className="mt-4 border-t border-border/60 pt-3 text-sm font-light text-muted-foreground">
                {triggerType === "scheduled" ? (
                  hasSchedule ? (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      <span className="inline-flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onSchedulePause?.()}
                          aria-label={
                            isScheduleActive
                              ? "Pause schedule"
                              : "Resume schedule"
                          }
                          className={`grid h-4 w-4 place-items-center rounded-full text-white ${
                            isScheduleActive ? "bg-emerald-600" : "bg-amber-500"
                          }`}
                        >
                          {isScheduleActive ? (
                            <Play
                              className="h-2 w-2"
                              fill="currentColor"
                              stroke="none"
                            />
                          ) : (
                            <Pause
                              className="h-2 w-2"
                              fill="currentColor"
                              stroke="none"
                            />
                          )}
                        </button>
                        <span className="font-medium text-foreground">
                          {scheduleSummary}
                        </span>
                      </span>
                      <span className="text-border">·</span>
                      <span>
                        Next run{" "}
                        <span className="font-medium text-foreground">
                          {nextRunLabel || "—"}
                        </span>
                      </span>
                      <div className="ml-auto flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => setShowScheduleModal(true)}
                          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                        >
                          <Pencil className="h-3 w-3" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={clearSchedule}
                          className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-3 w-3" /> Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CalendarClock className="h-3.5 w-3.5" />
                      <span>Pick a cadence next.</span>
                      <button
                        type="button"
                        onClick={() => setShowScheduleModal(true)}
                        className="ml-auto inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="h-3 w-3" /> Choose cadence
                      </button>
                    </div>
                  )
                ) : triggerType === "webhook" ? (
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" />
                    <span>Set up your webhook trigger below.</span>
                  </div>
                ) : triggerPicked ? (
                  <div className="flex items-center gap-2">
                    <Play className="h-3.5 w-3.5" />
                    <span>
                      Run on demand from{" "}
                      <span className="font-medium text-foreground">
                        Your Tasks
                      </span>
                      . Results then appear in Slack or Teams.
                    </span>
                  </div>
                ) : (
                  <div className="flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
                    <span className="page-eyebrow">Suggested</span>
                    <span className="truncate font-medium text-foreground/80">
                      {suggestAutomation(promptName, promptContent)}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Webhook endpoint picker */}
            {triggerType === "webhook" && (
              <section
                className="mb-6 mt-6 rounded-3xl bg-card px-5 py-6 shadow-[0_20px_50px_hsl(var(--foreground)/0.04)] ring-1 ring-foreground/5 sm:px-8 sm:py-7"
                aria-labelledby="webhook-endpoint-heading"
              >
                <div
                  id="webhook-endpoint-heading"
                  className="mb-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground"
                >
                  <Sparkles className="h-3 w-3" />
                  Webhook endpoint
                </div>
                <p className="mb-4 text-sm font-light leading-relaxed text-muted-foreground">
                  Choose an existing endpoint or create a new one to trigger this
                  Task.
                </p>
                <div className="relative" ref={webhookMenuRef}>
                  <button
                    type="button"
                    onClick={() => setShowWebhookMenu((value) => !value)}
                    className="flex h-[42px] w-full items-center justify-between rounded-md border border-border bg-background px-3 text-sm font-light text-foreground hover:border-foreground/40"
                  >
                    <span
                      className={
                        selectedWebhook
                          ? "text-foreground"
                          : "text-muted-foreground"
                      }
                    >
                      {selectedWebhook
                        ? selectedWebhook.webhook_name
                        : "Select a webhook"}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  </button>

                  {showWebhookMenu && (
                    <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-20 overflow-hidden rounded-md border border-border bg-popover p-1 shadow-md">
                      {webhooksLoading ? (
                        <div className="flex h-10 items-center px-3 text-sm font-light text-muted-foreground">
                          Loading webhooks…
                        </div>
                      ) : (
                        webhooks.map((hook) => (
                          <button
                            key={hook.source_id}
                            type="button"
                            onClick={() => {
                              setSelectedWebhookId(hook.source_id);
                              setShowWebhookMenu(false);
                            }}
                            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-light text-foreground hover:bg-muted"
                          >
                            {hook.source_id === selectedWebhookId ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <span className="h-4 w-4" />
                            )}
                            {hook.webhook_name}
                          </button>
                        ))
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          setShowWebhookMenu(false);
                          setShowCreateWebhook(true);
                        }}
                        className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-light text-foreground hover:bg-muted"
                      >
                        <Plus className="h-4 w-4" /> Create new
                      </button>
                    </div>
                  )}
                </div>
                {webhookError && (
                  <p className="mt-2 text-sm text-destructive">{webhookError}</p>
                )}
              </section>
            )}

            <hr className="my-6 border-border" />

            {/* Privacy + Sharing */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <div className="page-eyebrow mb-1">Privacy</div>
                <p className="mb-3 text-sm font-light text-muted-foreground">
                  {promptSetAs === "shared"
                    ? "This Task is visible to your teammates and can be shared."
                    : "This Task is only visible to you. Teammates can’t see or edit it."}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => onPromptSetAsChange("shared")}
                    aria-pressed={promptSetAs === "shared"}
                    className={`interactive-surface inline-flex h-[42px] items-center gap-2 whitespace-nowrap rounded-md border border-border px-4 text-sm font-medium text-foreground ${
                      promptSetAs === "shared" ? "bg-muted" : "bg-background"
                    }`}
                  >
                    {promptSetAs === "shared" && (
                      <span className="grid h-4 w-4 place-items-center rounded-full bg-foreground text-background">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                    )}
                    <Eye className="h-3.5 w-3.5" /> Visible to team
                  </button>
                  <button
                    type="button"
                    onClick={() => onPromptSetAsChange("personal")}
                    aria-pressed={promptSetAs === "personal"}
                    className={`interactive-surface inline-flex h-[42px] items-center gap-2 whitespace-nowrap rounded-md border border-border px-4 text-sm font-medium text-foreground ${
                      promptSetAs === "personal" ? "bg-muted" : "bg-background"
                    }`}
                  >
                    {promptSetAs === "personal" && (
                      <span className="grid h-4 w-4 place-items-center rounded-full bg-foreground text-background">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                    )}
                    <Lock className="h-3.5 w-3.5" /> Only me
                  </button>
                </div>
              </div>

              <div>
                <div className="page-eyebrow">
                  Shared with ({sharedUsers.length})
                </div>
                <p className="mb-3 mt-1 text-sm font-light text-muted-foreground">
                  Sharing creates a copy of this Task
                </p>
                {sharedUsers.length > 0 ? (
                  <div className="flex items-center -space-x-2">
                    {visibleSharedUsers.map((user) => (
                      <Tooltip key={user?.id || user?._id || user?.username}>
                        <TooltipTrigger asChild>
                          <span className="grid h-8 w-8 cursor-default place-items-center rounded-full bg-muted text-xs font-medium uppercase text-muted-foreground ring-2 ring-card">
                            {(user?.username || "?").charAt(0)}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="capitalize">
                          {user?.username}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                    {remainingSharedUsers > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowShareModal(true)}
                        className="interactive-chip ml-3 inline-flex h-8 items-center rounded-full border border-border bg-background px-3 text-xs font-medium text-foreground"
                      >
                        +{remainingSharedUsers} more
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-sm font-light italic text-muted-foreground">
                    Not shared yet
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => setShowShareModal(true)}
                  disabled={!isEdit}
                  className="interactive-surface mt-3 inline-flex h-[42px] items-center gap-2 whitespace-nowrap rounded-md border border-border bg-background px-4 text-sm font-medium text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Share2 className="h-3.5 w-3.5" /> Share Task
                </button>
              </div>
            </div>

            {/* Estimated time saved */}
            <div className="mt-6">
              <div className="page-eyebrow">Estimated time saved</div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-light text-muted-foreground">
                Based on similar tasks
                <Input
                  type="number"
                  min={0}
                  value={estimatedTime ?? 1}
                  onChange={(event) =>
                    onEstimatedTimeChange?.(event.target.value)
                  }
                  className="ml-2 w-14 px-2 text-center text-sm font-light"
                  style={{ height: 42 }}
                />
                <span>hours</span>
              </div>
            </div>
          </div>

          {/* Right rail — "Building your Task" */}
          <aside className="sticky top-4 hidden w-[320px] shrink-0 self-start lg:block">
            <div className="rounded-2xl border border-border/60 bg-card p-6 shadow-[0_20px_50px_hsl(var(--foreground)/0.04)]">
              {/* Mono eyebrow (10px / 0.18em), not .page-eyebrow — matches the
                  Lovable rail heading. */}
              <div className="font-mono-display text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                Building your Task
              </div>
              <div className="mt-3 flex items-center gap-3">
                <span className="text-sm font-light text-muted-foreground">
                  ~{estimatedTime || 0}hrs saved
                </span>
                {showChannelPicker && outputOption?.value && (
                  <ToolIcon type={outputOption.value} />
                )}
              </div>
              <hr className="my-4 border-border/60" />
              <dl className="space-y-3 text-sm">
                {railRows.map((row) => (
                  <div
                    key={row.label}
                    className="flex items-center justify-between gap-3"
                  >
                    <dt className="font-light text-muted-foreground">
                      {row.label}
                    </dt>
                    <dd className="flex min-w-0 items-center gap-1.5 font-medium text-foreground">
                      {row.done && (
                        <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full bg-foreground text-background">
                          <Check className="h-2.5 w-2.5" />
                        </span>
                      )}
                      <span className="truncate">{row.value}</span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>

        {/* Ready-when-you-are bar */}
        {isEdit && (
          <div className="sticky bottom-0 z-30 mt-8 border-t border-border bg-background/95 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
              <div className="flex min-w-0 items-start gap-3 sm:items-center">
                <Play className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground sm:mt-0" />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    Ready when you are
                  </div>
                  <div className="mt-0.5 hidden flex-wrap items-center gap-1 text-xs text-muted-foreground sm:flex">
                    You can also run this from
                    <img src={slackIcon} alt="" className="h-3 w-3" aria-hidden />
                    Slack or
                    <img src={teamsIcon} alt="" className="h-3 w-3" aria-hidden />
                    Teams once connected by saying
                    <span className="font-medium text-foreground">@maybe</span>
                    in any thread.
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground sm:hidden">
                    Also runs from
                    <img src={slackIcon} alt="" className="h-3 w-3" aria-hidden />
                    <img src={teamsIcon} alt="" className="h-3 w-3" aria-hidden />
                    via <span className="font-medium text-foreground">@maybe</span>.
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={handleRunNow}
                disabled={runningTask}
                className="flex h-11 w-full shrink-0 items-center justify-center gap-2 rounded-xl border border-foreground bg-foreground px-5 text-sm font-semibold text-background shadow-lg shadow-foreground/10 transition-colors hover:bg-background hover:text-foreground active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {runningTask ? "Running…" : "Run this Task"}
              </button>
            </div>
          </div>
        )}

        <CreateScheduleModal
          open={showScheduleModal}
          initial={scheduleConfig}
          onClose={() => setShowScheduleModal(false)}
          onCreate={handleScheduleCreate}
          isLive={isScheduleActive}
          onToggleLive={() => onSchedulePause?.()}
        />

        <ShareChatModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          contact={editadata}
          shareType="task"
          resourceTitle={editadata?.title || promptName}
          onSuccess={getUpdatePromptsData}
        />

        {/* Create Webhook Modal */}
        {showCreateWebhook && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
            onClick={() => {
              setShowCreateWebhook(false);
              setNewWebhookName("");
            }}
          >
            <div
              className="w-[440px] max-w-[92vw] rounded-lg bg-card p-6 shadow-xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-3 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-mono-display text-[16px] font-semibold text-foreground">
                    Create webhook
                  </h3>
                  <p className="mt-1 text-[13px] font-light text-muted-foreground">
                    Create a new webhook endpoint to receive events from external
                    services.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => {
                    setShowCreateWebhook(false);
                    setNewWebhookName("");
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className="mt-4">
                <div className="page-eyebrow mb-2">Webhook name</div>
                <Input
                  autoFocus
                  type="text"
                  value={newWebhookName}
                  onChange={(event) => setNewWebhookName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleCreateWebhook();
                    }
                  }}
                  placeholder="e.g. Fireflies integration"
                  className="w-full px-3 text-sm"
                  style={{ height: 42 }}
                />
                <p className="mt-2 text-[12px] font-light text-muted-foreground">
                  Give your webhook a descriptive name to identify it later.
                </p>
                {webhookError && (
                  <p className="mt-2 text-sm text-destructive">{webhookError}</p>
                )}
              </div>

              <div className="mt-6 flex items-center justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateWebhook(false);
                    setNewWebhookName("");
                  }}
                  className="px-4 text-[13px]"
                  style={{ height: 42 }}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  disabled={!newWebhookName.trim() || creatingWebhook}
                  onClick={handleCreateWebhook}
                  className="px-5 text-[13px] font-medium"
                  style={{ height: 42 }}
                >
                  {creatingWebhook ? "Creating…" : "Create"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default PromptDetailsForm;
